import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAssignees } from "@/api/hooks";
import { useChecklistTemplates, useRecurrences, useTaskTemplates, useTemplateMutations } from "@/api/templates";
import type { ChecklistTemplate } from "@/api/types/ChecklistTemplate";
import type { Project } from "@/api/types/Project";
import type { Recurrence } from "@/api/types/Recurrence";
import type { RecurrenceRule } from "@/api/types/RecurrenceRule";
import type { TaskPriority } from "@/api/types/TaskPriority";
import type { TaskTemplate } from "@/api/types/TaskTemplate";
import type { Weekday } from "@/api/types/Weekday";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { useToast } from "@/components/Toast";
import { Button, Checkbox, Field, Input, Notice, Select, Textarea } from "@/components/ui";
import { PRIORITIES } from "@/lib/work";

const WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function Card({ title, intro, action, children }: { title: string; intro: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-1">
          <h3 className="m-0 label-caps">{title}</h3>
          <p className="m-0 text-sm text-muted">{intro}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Lines of a textarea, blanks dropped. */
const lines = (s: string) =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

// ───────────── Task templates ─────────────

export function TaskTemplatesSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const list = useTaskTemplates(project.key);
  const m = useTemplateMutations(project.key);
  const [editing, setEditing] = useState<TaskTemplate | "new" | null>(null);
  const [deleting, setDeleting] = useState<TaskTemplate | null>(null);
  return (
    <Card
      title={t("templates.taskTemplates")}
      intro={t("templates.taskTemplatesIntro")}
      action={
        <Button size="sm" icon="plus" onClick={() => setEditing("new")}>
          {t("templates.new")}
        </Button>
      }
    >
      {list.data?.length ? (
        <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-card)] border border-border p-0">
          {list.data.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <b>{x.name}</b>
              <span className="min-w-0 flex-1 truncate text-muted">
                {x.title}
                {x.checklists.length > 0 && ` · ${t("templates.nChecklists", { count: x.checklists.length })}`}
                {x.subtasks.length > 0 && ` · ${t("templates.nSubtasks", { count: x.subtasks.length })}`}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(x)}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="ghost" icon="trash" aria-label={t("common.delete")} onClick={() => setDeleting(x)} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-sm text-muted">{t("templates.none")}</p>
      )}
      {editing && <TemplateDialog project={project} template={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={t("templates.deleteTitle", { name: deleting?.name ?? "" })}
        body={t("templates.deleteBody")}
        confirmLabel={t("common.delete")}
        danger
        loading={m.removeTask.isPending}
        onConfirm={() => deleting && m.removeTask.mutate(deleting.id, { onSuccess: () => setDeleting(null), onError: (e) => toast.error(e) })}
      />
    </Card>
  );
}

function TemplateDialog({ project, template, onClose }: { project: Project; template: TaskTemplate | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const m = useTemplateMutations(project.key);
  const [name, setName] = useState(template?.name ?? "");
  const [title, setTitle] = useState(template?.title ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [priority, setPriority] = useState<TaskPriority | "">(template?.priority ?? "");
  const [points, setPoints] = useState(template?.estimate_points?.toString() ?? "");
  // One checklist per block: its name on the first line, items below; blocks split by a blank line.
  const [checklists, setChecklists] = useState((template?.checklists ?? []).map((c) => [c.name, ...c.items].join("\n")).join("\n\n"));
  const [subtasks, setSubtasks] = useState((template?.subtasks ?? []).join("\n"));
  const ok = name.trim() && title.trim();
  const submit = () => {
    if (!ok) return;
    const blocks = checklists
      .split(/\n\s*\n/)
      .map(lines)
      .filter((b) => b.length > 0)
      .map(([n, ...items]) => ({ name: n!, items }));
    m.saveTask.mutate(
      {
        id: template?.id,
        name: name.trim(),
        title: title.trim(),
        description,
        priority: priority || undefined,
        tag_ids: template?.tag_ids ?? [],
        estimate_points: project.estimate_unit === "points" && points.trim() ? Number(points) : undefined,
        estimate_minutes: template?.estimate_minutes ?? undefined,
        checklists: blocks,
        subtasks: lines(subtasks),
      },
      { onSuccess: onClose, onError: (e) => toast.error(e) },
    );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      width={620}
      title={template ? t("templates.edit") : t("templates.new")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={m.saveTask.isPending} disabled={!ok} onClick={submit}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("templates.name")}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />}</Field>
        <Field label={t("templates.taskTitle")}>{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} />}</Field>
      </div>
      <Field label={t("tasks.description")} optional>
        {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
      </Field>
      <div className="flex flex-wrap gap-4">
        <Field label={t("work.priority")} optional>
          {(p) => (
            <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority | "")} className="w-40">
              <option value="">{t("work.priorities.none")}</option>
              {PRIORITIES.map((x) => (
                <option key={x} value={x}>
                  {t(`work.priorities.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {project.scrum_enabled && project.estimate_unit === "points" && (
          <Field label={t("sprints.estimate")} optional>
            {(p) => <Input {...p} type="number" min={0} value={points} onChange={(e) => setPoints(e.target.value)} className="w-28" />}
          </Field>
        )}
      </div>
      <Field label={t("templates.checklists")} hint={t("templates.checklistsHint")} optional>
        {(p) => <Textarea {...p} value={checklists} onChange={(e) => setChecklists(e.target.value)} rows={5} className="font-mono text-sm" />}
      </Field>
      <Field label={t("templates.subtasks")} hint={t("templates.subtasksHint")} optional>
        {(p) => <Textarea {...p} value={subtasks} onChange={(e) => setSubtasks(e.target.value)} rows={3} />}
      </Field>
    </Dialog>
  );
}

// ───────────── Checklist templates ─────────────

export function ChecklistTemplatesSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const list = useChecklistTemplates(project.key);
  const m = useTemplateMutations(project.key);
  const [editing, setEditing] = useState<ChecklistTemplate | "new" | null>(null);
  return (
    <Card
      title={t("templates.checklistTemplates")}
      intro={t("templates.checklistTemplatesIntro")}
      action={
        <Button size="sm" icon="plus" onClick={() => setEditing("new")}>
          {t("templates.newChecklist")}
        </Button>
      }
    >
      {list.data?.length ? (
        <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-card)] border border-border p-0">
          {list.data.map((x) => (
            <li key={x.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <b>{x.name}</b>
              <span className="flex-1 text-muted">{t("templates.nItems", { count: x.items.length })}</span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(x)}>
                {t("common.edit")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon="trash"
                aria-label={t("common.delete")}
                onClick={() => m.removeChecklist.mutate(x.id, { onError: (e) => toast.error(e) })}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-sm text-muted">{t("templates.noneChecklists")}</p>
      )}
      {editing && <ChecklistDialog project={project} template={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function ChecklistDialog({ project, template, onClose }: { project: Project; template: ChecklistTemplate | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const m = useTemplateMutations(project.key);
  const [name, setName] = useState(template?.name ?? "");
  const [items, setItems] = useState((template?.items ?? []).join("\n"));
  const submit = () =>
    name.trim() &&
    m.saveChecklist.mutate({ id: template?.id, name: name.trim(), items: lines(items) }, { onSuccess: onClose, onError: (e) => toast.error(e) });
  return (
    <Dialog
      open
      onClose={onClose}
      title={template ? t("templates.editChecklist") : t("templates.newChecklist")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={m.saveChecklist.isPending} disabled={!name.trim()} onClick={submit}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("templates.name")}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />}</Field>
      <Field label={t("templates.items")} hint={t("templates.itemsHint")}>
        {(p) => <Textarea {...p} value={items} onChange={(e) => setItems(e.target.value)} rows={6} />}
      </Field>
    </Dialog>
  );
}

// ───────────── Recurring tasks ─────────────

export function RecurringSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const list = useRecurrences(project.key);
  const templates = useTaskTemplates(project.key);
  const m = useTemplateMutations(project.key);
  const [editing, setEditing] = useState<Recurrence | "new" | null>(null);
  const describe = (r: RecurrenceRule) =>
    r.kind === "daily"
      ? r.weekdays.length
        ? t("templates.rule.dailyOn", { days: r.weekdays.map((d) => t(`sprintSettings.dayLong.${d}`)).join(", ") })
        : t("templates.rule.daily")
      : r.kind === "weekly"
        ? t("templates.rule.weekly", { day: t(`sprintSettings.dayLong.${r.weekday}`) })
        : t("templates.rule.monthly", { day: r.day });
  return (
    <Card
      title={t("templates.recurring")}
      intro={t("templates.recurringIntro")}
      action={
        <Button size="sm" icon="plus" disabled={!templates.data?.length} onClick={() => setEditing("new")}>
          {t("templates.newRule")}
        </Button>
      }
    >
      {!templates.data?.length && <p className="m-0 text-sm text-muted">{t("templates.needTemplate")}</p>}
      {list.data?.length ? (
        <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-card)] border border-border p-0">
          {list.data.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <b>{r.template_name}</b>
                <span className="text-muted">
                  {describe(r.rule)} · {t("templates.atHour", { hour: String(r.hour).padStart(2, "0") })}
                  {r.assignee && ` · ${r.assignee.display_name}`}
                  {r.assignee?.is_bot && ` (${t("common.bot")})`}
                  {project.scrum_enabled && r.placement === "active_sprint" && ` · ${t("templates.intoSprint")}`}
                </span>
                <span className="ml-auto flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      m.saveRecurrence.mutate(
                        {
                          id: r.id,
                          template_id: r.template_id,
                          rule: r.rule,
                          hour: r.hour,
                          assignee_id: r.assignee?.id,
                          placement: r.placement,
                          skip_if_open: r.skip_if_open,
                          paused: !r.paused,
                        },
                        { onError: (e) => toast.error(e) },
                      )
                    }
                  >
                    {r.paused ? t("templates.resume") : t("templates.pause")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                    {t("common.edit")}
                  </Button>
                  <Button size="sm" variant="ghost" icon="trash" aria-label={t("common.delete")} onClick={() => m.removeRecurrence.mutate(r.id, { onError: (e) => toast.error(e) })} />
                </span>
              </div>
              <span className="text-xs text-muted">
                {r.paused
                  ? r.paused_reason
                    ? t("templates.pausedBecause", { reason: r.paused_reason })
                    : t("templates.paused")
                  : t("templates.nextRun", { when: new Date(r.next_run_at).toLocaleString() })}
                {r.last_task_key && ` · ${t("templates.lastTask", { key: r.last_task_key })}`}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        templates.data?.length && <p className="m-0 text-sm text-muted">{t("templates.noRules")}</p>
      )}
      {editing && <RuleDialog project={project} rule={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function RuleDialog({ project, rule, onClose }: { project: Project; rule: Recurrence | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const templates = useTaskTemplates(project.key);
  const members = useAssignees(project.key);
  const m = useTemplateMutations(project.key);
  const [template, setTemplate] = useState(rule?.template_id ?? templates.data?.[0]?.id ?? "");
  const [kind, setKind] = useState<RecurrenceRule["kind"]>(rule?.rule.kind ?? "weekly");
  const [days, setDays] = useState<Weekday[]>(rule?.rule.kind === "daily" ? rule.rule.weekdays : ["mon", "tue", "wed", "thu", "fri"]);
  const [weekday, setWeekday] = useState<Weekday>(rule?.rule.kind === "weekly" ? rule.rule.weekday : "mon");
  const [dom, setDom] = useState(rule?.rule.kind === "monthly" ? rule.rule.day : 1);
  const [hour, setHour] = useState(rule?.hour ?? 9);
  const [assignee, setAssignee] = useState(rule?.assignee?.id ?? "");
  const [sprint, setSprint] = useState(rule?.placement === "active_sprint");
  const [skip, setSkip] = useState(rule?.skip_if_open ?? true);
  const value: RecurrenceRule = kind === "daily" ? { kind, weekdays: days } : kind === "weekly" ? { kind, weekday } : { kind, day: dom };
  const submit = () =>
    template &&
    m.saveRecurrence.mutate(
      {
        id: rule?.id,
        template_id: template,
        rule: value,
        hour,
        assignee_id: assignee || undefined,
        placement: sprint ? "active_sprint" : "backlog",
        skip_if_open: skip,
        paused: rule?.paused ?? false,
      },
      { onSuccess: onClose, onError: (e) => toast.error(e) },
    );
  return (
    <Dialog
      open
      onClose={onClose}
      title={rule ? t("templates.editRule") : t("templates.newRule")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={m.saveRecurrence.isPending} disabled={!template} onClick={submit}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("templates.template")}>
        {(p) => (
          <Select {...p} value={template} onChange={(e) => setTemplate(e.target.value)}>
            {(templates.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("templates.repeat")}>
          {(p) => (
            <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as RecurrenceRule["kind"])} className="w-40">
              <option value="daily">{t("templates.rule.kindDaily")}</option>
              <option value="weekly">{t("templates.rule.kindWeekly")}</option>
              <option value="monthly">{t("templates.rule.kindMonthly")}</option>
            </Select>
          )}
        </Field>
        {kind === "weekly" && (
          <Field label={t("templates.on")}>
            {(p) => (
              <Select {...p} value={weekday} onChange={(e) => setWeekday(e.target.value as Weekday)} className="w-36">
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>
                    {t(`sprintSettings.dayLong.${d}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {kind === "monthly" && (
          <Field label={t("templates.dayOfMonth")}>
            {(p) => <Input {...p} type="number" min={1} max={31} value={dom} onChange={(e) => setDom(Number(e.target.value))} className="w-24" />}
          </Field>
        )}
        <Field label={t("templates.hour")}>
          {(p) => <Input {...p} type="number" min={0} max={23} value={hour} onChange={(e) => setHour(Number(e.target.value))} className="w-20" />}
        </Field>
      </div>
      {kind === "daily" && (
        <fieldset className="m-0 flex flex-wrap gap-3 border-0 p-0">
          <legend className="mb-1.5 text-sm font-medium">{t("templates.onDays")}</legend>
          {WEEKDAYS.map((d) => (
            <Checkbox key={d} label={t(`sprintSettings.dayLong.${d}`)} checked={days.includes(d)} onChange={(e) => setDays((cur) => (e.target.checked ? [...cur, d] : cur.filter((x) => x !== d)))} />
          ))}
        </fieldset>
      )}
      <Field label={t("work.assignees")} optional>
        {(p) => (
          <Select {...p} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">{t("work.nobody")}</option>
            {(members.data ?? [])
              .filter((x) => !x.disabled)
              .map((x) => (
                <option key={x.user_id} value={x.user_id}>
                  {x.display_name}
                  {x.is_bot && ` · ${t("common.bot")}`}
                </option>
              ))}
          </Select>
        )}
      </Field>
      {project.scrum_enabled && <Checkbox label={t("templates.intoSprintLabel")} checked={sprint} onChange={(e) => setSprint(e.target.checked)} />}
      <Checkbox label={t("templates.skipIfOpen")} checked={skip} onChange={(e) => setSkip(e.target.checked)} />
      <Notice>{t("templates.ruleHint")}</Notice>
    </Dialog>
  );
}
